import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PeopleIcon from '@material-ui/icons/People';
import SearchIcon from '@material-ui/icons/Search';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  GroupEntity,
  UserEntity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Content, Header, Link, Page } from '@backstage/core-components';
import { buildMemberCountMap } from '../../utils/buildMemberCountMap';
import { entityRefToCatalogPath } from '../shared/entityRefs';
import { useCanEditTeam } from '../../hooks/useCanEditTeam';

const useStyles = makeStyles(theme => ({
  searchBox: {
    maxWidth: 480,
  },
  treeRoot: {
    padding: theme.spacing(0),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1, 1, 0),
    borderRadius: theme.shape.borderRadius,
    cursor: 'default',
    '&:hover': {
      background: theme.palette.action.hover,
    },
    gap: theme.spacing(0.5),
  },
  chevron: {
    padding: theme.spacing(0.5),
    flexShrink: 0,
    width: 32,
    height: 32,
  },
  chevronPlaceholder: {
    width: 32,
    height: 32,
    flexShrink: 0,
  },
  teamName: {
    fontWeight: 600,
    fontSize: '0.95rem',
    lineHeight: 1.2,
    flexShrink: 0,
  },
  mission: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexGrow: 1,
    minWidth: 0,
    marginLeft: theme.spacing(1),
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
    marginLeft: 'auto',
    paddingLeft: theme.spacing(1),
  },
  memberChip: {
    height: 22,
    fontSize: '0.75rem',
  },
  lead: {
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  leadAvatar: {
    width: 22,
    height: 22,
    fontSize: '0.6rem',
    backgroundColor: theme.palette.primary.main,
  },
  actionIcon: {
    padding: theme.spacing(0.5),
    opacity: 0,
    '$row:hover &': {
      opacity: 1,
    },
  },
  indent: {
    marginLeft: theme.spacing(2),
    borderLeft: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(1),
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  typeChip: {
    height: 18,
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  // ownsAssets marker — matches the amber square used in the Team Data
  // Model v2 diagram legend, deliberately not a text chip since it's meant
  // to read at a glance across a long tree.
  ownsAssetsMarker: {
    width: 10,
    height: 10,
    borderRadius: 2,
    backgroundColor: theme.palette.warning.main,
    flexShrink: 0,
  },
  // Brand P1/White pair (colors/color-codes.md) — 12.2:1 contrast, so it
  // reads the same in both light and dark theme rather than relying on
  // theme.palette colors that shift with the mode.
  highlight: {
    backgroundColor: '#5F016F',
    color: '#FFFFFF',
    borderRadius: 2,
    padding: '0 2px',
  },
}));

interface TreeNode {
  entity: GroupEntity;
  children: TreeNode[];
}

interface LeadRefEntry {
  name: string;
  picture?: string;
  entityName: string;
}

const LEVEL_LABELS: Record<string, string> = {
  company: 'Company',
  function: 'Function',
  'sub-function': 'Sub-Function',
  xp: 'XP',
};

function buildTree(entities: GroupEntity[]): TreeNode[] {
  const byName = new Map<string, TreeNode>(
    entities.map(e => [e.metadata.name, { entity: e, children: [] }]),
  );

  const roots: TreeNode[] = [];

  for (const node of byName.values()) {
    const parentRef = node.entity.spec?.parent as string | undefined;
    // spec.parent may be a plain name ("technology") or a full entity ref
    // ("group:default/technology") depending on catalog normalisation — handle both.
    const parentName = parentRef?.split('/').pop();
    if (parentName && byName.has(parentName)) {
      byName.get(parentName)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort children alphabetically by title
  const sortNodes = (nodes: TreeNode[]) => {
    nodes.sort((a, b) =>
      (a.entity.metadata.title ?? a.entity.metadata.name).localeCompare(
        b.entity.metadata.title ?? b.entity.metadata.name,
      ),
    );
    nodes.forEach(n => sortNodes(n.children));
  };
  sortNodes(roots);

  return roots;
}

// `team/level` is derived from `spec.type` at catalog read time by
// GroupTypeFlagsProcessor, so the annotation is what production sees. Test
// fixtures (and any entity ingested before that processor ran) only carry
// `spec.type`, hence the fallback.
function groupLevel(node: TreeNode): string | undefined {
  return (
    node.entity.metadata.annotations?.['team/level'] ??
    (node.entity.spec?.type as string | undefined)
  );
}

/**
 * Seeds the tree's initially-expanded nodes (keyed by `metadata.name`).
 *
 * Depth-0 roots are always open already (see `TreeRow`'s `depth < 1` seed), so
 * in the real catalog — a single `company` root whose children are the
 * functions — the first level that actually costs a click is the company's
 * children. When there is only one of those, there is no choice to present, so
 * open it automatically and reveal its sub-functions.
 *
 * The company root is stepped over rather than counted: a lone company root is
 * not a meaningful "only one option", its single child is. Trees without a
 * company wrapper are evaluated at root level instead, where a single root is
 * already open and this becomes a no-op.
 *
 * Deliberately one level deep only — no cascade into grandchildren, otherwise a
 * narrow-but-deep org would unfold entirely on load.
 */
function computeInitialExpanded(roots: TreeNode[]): Set<string> {
  const topLevel =
    roots.length === 1 && groupLevel(roots[0]) === 'company'
      ? roots[0].children
      : roots;

  return topLevel.length === 1
    ? new Set([topLevel[0].entity.metadata.name])
    : new Set<string>();
}

const MISSION_VISIBLE_CHARS = 60;
const MISSION_EXCERPT_RADIUS = 30;

// The mission Typography is a single truncated line, so a match past the
// first ~60 chars would otherwise be invisible without hovering the tooltip.
// Re-center the snippet on the match in that case instead of the fixed
// start-of-string truncation.
function getMissionSnippet(mission: string, query: string): string {
  if (!query) return mission;
  const idx = mission.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1 || idx + query.length <= MISSION_VISIBLE_CHARS) return mission;

  const start = Math.max(0, idx - MISSION_EXCERPT_RADIUS);
  const end = Math.min(
    mission.length,
    idx + query.length + MISSION_EXCERPT_RADIUS,
  );
  return `${start > 0 ? '…' : ''}${mission.slice(start, end)}${end < mission.length ? '…' : ''}`;
}

function highlightMatch(
  text: string,
  query: string,
  highlightClassName: string,
) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className={highlightClassName}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function matchesSearch(node: TreeNode, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const { name, title, description } = node.entity.metadata;
  if ((title ?? name).toLowerCase().includes(q)) return true;
  if (description?.toLowerCase().includes(q)) return true;
  if (node.children.some(c => matchesSearch(c, query))) return true;
  return false;
}

// PAT-798: the teams view is a route, so navigating to the edit page unmounts
// it and discards its component state. Mirror the user's view state (expanded
// nodes + search text) into sessionStorage keyed by this tab's location key so
// the browser's back button restores the exact view the user left — while a
// fresh visit to /teams (or a re-opened tab) starts from the defaults.
const VIEW_STATE_SESSION_KEY = 'team-management:teams-view-state';
const VIEW_STATE_TAB_KEY = 'team-management:teams-view-state-key';

interface TeamsViewState {
  open?: string[];
  closed?: string[];
  search?: string;
}

// Some browser privacy modes throw on merely *accessing* window.sessionStorage,
// not just on getItem/setItem, so the access itself needs guarding too.
function getSessionStorage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function readStoredViewState(
  session: Storage | undefined,
  tabKey: string | undefined,
): TeamsViewState {
  if (!session || !tabKey || session.getItem(VIEW_STATE_TAB_KEY) !== tabKey) {
    return {};
  }
  try {
    return JSON.parse(session.getItem(VIEW_STATE_SESSION_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeStoredViewState(
  session: Storage | undefined,
  tabKey: string,
  state: TeamsViewState,
) {
  try {
    session?.setItem(VIEW_STATE_TAB_KEY, tabKey);
    session?.setItem(VIEW_STATE_SESSION_KEY, JSON.stringify(state));
  } catch {
    // Storage full or blocked — restoring the view just silently stops working.
  }
}

interface TreeRowProps {
  node: TreeNode;
  depth: number;
  searchQuery: string;
  leadPictureMap: Map<string, string>;
  leadEntityMap: Map<string, string>;
  leadRefMap: Map<string, LeadRefEntry>;
  memberCountMap: Map<string, number>;
  readOnly: boolean;
  initialExpanded: Set<string>;
  // Page-owned expand/collapse state, lifted out of this row so it can be
  // persisted and restored across client-side navigations (PAT-798).
  openOverrides: Record<string, boolean>;
  onToggle: (name: string, open: boolean) => void;
  // `?addMember=…` (or '') carried onto every edit link; computed once by the page.
  editLinkSuffix: string;
}

function TreeRow({
  node,
  depth,
  searchQuery,
  leadPictureMap,
  leadEntityMap,
  leadRefMap,
  memberCountMap,
  readOnly,
  initialExpanded,
  openOverrides,
  onToggle,
  editLinkSuffix,
}: TreeRowProps) {
  const classes = useStyles();
  const { entity, children } = node;
  // Roots default to open; `initialExpanded` additionally opens the single
  // top-level function when there is only one (see computeInitialExpanded).
  // An override records a manual toggle so it survives remounts; rows without
  // one fall back to the seed.
  const open =
    openOverrides[entity.metadata.name] ??
    (depth < 1 || initialExpanded.has(entity.metadata.name));

  // When searching, auto-expand to show matches
  const hasMatchingDescendant =
    searchQuery && children.some(c => matchesSearch(c, searchQuery));
  const effectiveOpen = searchQuery ? hasMatchingDescendant || open : open;

  const annotations = entity.metadata.annotations ?? {};
  const leadEmail = annotations['team/lead-email'];
  const leadRef = annotations['team/lead']; // e.g. "user:default/joel-harley"

  let leadName: string | undefined;
  let leadPicture: string | undefined;
  let leadUrl: string | undefined;

  if (leadRef) {
    // New format: entity-ref. Resolve from preloaded users; fall back gracefully.
    const resolved = leadRefMap.get(leadRef.toLowerCase());
    leadName = resolved?.name ?? leadRef.split('/').pop();
    leadPicture = annotations['team/lead-picture'] || resolved?.picture;
    leadUrl = entityRefToCatalogPath(leadRef);
  } else if (leadEmail) {
    // Legacy format: email — existing behavior preserved.
    leadPicture =
      annotations['team/lead-picture'] || leadPictureMap.get(leadEmail);
    const leadEntityName = leadEntityMap.get(leadEmail);
    leadUrl = leadEntityName
      ? `/catalog/default/user/${leadEntityName}`
      : undefined;
    leadName = leadEmail
      .split('@')[0]
      .split(/[.\-_]/)
      .map(w =>
        w.length <= 3 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1),
      )
      .join(' ');
  }

  const leadInitials = leadName
    ? leadName
        .split(' ')
        .map(w => w[0])
        .join('')
        .slice(0, 2)
    : undefined;
  const memberCount = memberCountMap.get(entity.metadata.name) ?? 0;
  const mission = entity.metadata.description;
  const entityUrl = `/catalog/${entity.metadata.namespace ?? 'default'}/group/${entity.metadata.name}`;
  const editUrl = `/team-edit/${entity.metadata.namespace ?? 'default'}/group/${entity.metadata.name}${editLinkSuffix}`;
  const isArchived = annotations['backstage.io/lifecycle'] === 'deprecated';
  // Team Data Model v2 classification (PAT-676) — team/level describes what
  // a parent-level group represents, ownsAssets marks a leaf team that owns
  // assets (not limited to dev teams). No badge for level=team — that's the
  // default, not a classification worth calling out.
  const level = annotations['team/level'];
  const canEdit = useCanEditTeam(
    level === 'company' ? '' : entity.metadata.name,
    entity.metadata.namespace ?? 'default',
  );
  const levelLabel = level ? LEVEL_LABELS[level] : undefined;
  const ownsAssets = annotations['team/owns-assets'] === 'true';

  if (searchQuery && !matchesSearch(node, searchQuery)) return null;

  return (
    <Box>
      <Box className={classes.row} style={{ opacity: isArchived ? 0.5 : 1 }}>
        {/* Expand/collapse chevron */}
        {children.length > 0 ? (
          <IconButton
            size="small"
            className={classes.chevron}
            onClick={() => onToggle(entity.metadata.name, !open)}
            aria-label={effectiveOpen ? 'Collapse' : 'Expand'}
          >
            {effectiveOpen ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <span className={classes.chevronPlaceholder} />
        )}

        {/* Team name */}
        <Link to={entityUrl} className={classes.teamName}>
          {highlightMatch(
            entity.metadata.title ?? entity.metadata.name,
            searchQuery,
            classes.highlight,
          )}
        </Link>

        {/* Archived badge */}
        {isArchived && (
          <Chip label="archived" size="small" className={classes.typeChip} />
        )}

        {/* Team Data Model v2 classification badge (PAT-676/PAT-679) */}
        {levelLabel && (
          <Chip label={levelLabel} size="small" className={classes.typeChip} />
        )}
        {ownsAssets && (
          <Tooltip title="Owns assets">
            <span
              className={classes.ownsAssetsMarker}
              aria-label="Owns assets"
            />
          </Tooltip>
        )}

        {/* Mission snippet */}
        {mission && (
          <Tooltip title={mission} placement="top">
            <Typography className={classes.mission}>
              {highlightMatch(
                getMissionSnippet(mission, searchQuery),
                searchQuery,
                classes.highlight,
              )}
            </Typography>
          </Tooltip>
        )}

        {/* Right-side metadata */}
        <Box className={classes.meta}>
          {memberCount > 0 && (
            <Chip
              icon={<PeopleIcon style={{ fontSize: 12 }} />}
              label={memberCount}
              size="small"
              variant="outlined"
              className={classes.memberChip}
            />
          )}
          {leadName && (
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 6, width: 168, flexShrink: 0 }}
            >
              <Avatar className={classes.leadAvatar} src={leadPicture}>
                {leadInitials}
              </Avatar>
              {leadUrl ? (
                <Link
                  to={leadUrl}
                  className={classes.lead}
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {leadName}
                </Link>
              ) : (
                <Typography className={classes.lead} noWrap>
                  {leadName}
                </Typography>
              )}
            </Box>
          )}
          {/* Company root is never editable through this UI — the backend
              rejects it outright regardless, but don't offer the entry
              point at all for the one level that can never use it. */}
          {!readOnly && canEdit && level !== 'company' && (
            <Tooltip title="Edit team">
              <IconButton
                size="small"
                className={classes.actionIcon}
                component={Link}
                to={editUrl}
                aria-label="Edit team"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Children */}
      {children.length > 0 && (
        <Collapse in={effectiveOpen} unmountOnExit>
          <Box className={classes.indent}>
            {children.map(child => (
              <TreeRow
                key={child.entity.metadata.name}
                node={child}
                depth={depth + 1}
                searchQuery={searchQuery}
                leadPictureMap={leadPictureMap}
                leadEntityMap={leadEntityMap}
                leadRefMap={leadRefMap}
                memberCountMap={memberCountMap}
                readOnly={readOnly}
                initialExpanded={initialExpanded}
                openOverrides={openOverrides}
                onToggle={onToggle}
                editLinkSuffix={editLinkSuffix}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export function TeamsPage() {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);
  const readOnly =
    configApi.getOptionalBoolean('teamManagement.readOnly') ?? false;
  const [entities, setEntities] = useState<GroupEntity[]>([]);
  const [leadPictureMap, setLeadPictureMap] = useState<Map<string, string>>(
    new Map(),
  );
  const [leadEntityMap, setLeadEntityMap] = useState<Map<string, string>>(
    new Map(),
  );
  const [leadRefMap, setLeadRefMap] = useState<Map<string, LeadRefEntry>>(
    new Map(),
  );
  const [memberCountMap, setMemberCountMap] = useState<Map<string, number>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  // Seed the interactive view state from sessionStorage: on a browser-back
  // remount this restores the view the user left; on a fresh visit the
  // location key no longer matches and the defaults apply (PAT-798).
  const location = useLocation();
  const locationKey = location.key;
  const addMember = new URLSearchParams(location.search).get('addMember');
  const editLinkSuffix =
    addMember !== null ? `?addMember=${encodeURIComponent(addMember)}` : '';
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>(
    () => {
      const stored = readStoredViewState(getSessionStorage(), locationKey);
      return Object.fromEntries([
        ...(stored.open?.map(name => [name, true] as const) ?? []),
        ...(stored.closed?.map(name => [name, false] as const) ?? []),
      ]);
    },
  );
  const [search, setSearch] = useState(
    () => readStoredViewState(getSessionStorage(), locationKey).search ?? '',
  );

  useEffect(() => {
    const entries = Object.entries(openOverrides);
    const open = entries
      .filter(([, isOpen]) => isOpen)
      .map(([name]) => name)
      .sort();
    const closed = entries
      .filter(([, isOpen]) => !isOpen)
      .map(([name]) => name)
      .sort();
    writeStoredViewState(getSessionStorage(), locationKey, {
      open,
      closed,
      search,
    });
  }, [locationKey, openOverrides, search]);

  const handleToggle = useCallback((name: string, open: boolean) => {
    setOpenOverrides(prev => ({ ...prev, [name]: open }));
  }, []);

  useEffect(() => {
    const groupsPromise = catalogApi.getEntities({
      filter: { kind: 'Group' },
      fields: [
        'metadata.name',
        'metadata.title',
        'metadata.description',
        'metadata.namespace',
        'metadata.annotations',
        'spec.parent',
        'spec.members',
        'spec.type',
      ],
    });

    const usersPromise = catalogApi.getEntities({
      filter: { kind: 'User' },
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'spec.profile',
        'spec.memberOf',
      ],
    });

    Promise.all([groupsPromise, usersPromise])
      .then(([groupsRes, usersRes]) => {
        // Archived teams don't belong in this view at all — they're findable
        // via the catalog entity page directly, and stay visible there via
        // the Archived badge. Any still-active child of an archived group
        // (shouldn't normally happen — archiving is blocked while active
        // assets exist) becomes a root in buildTree rather than disappearing.
        const activeGroups = (groupsRes.items as GroupEntity[]).filter(
          e =>
            e.metadata.annotations?.['backstage.io/lifecycle'] !== 'deprecated',
        );
        setEntities(activeGroups);
        const pictureMap = new Map<string, string>();
        const entityMap = new Map<string, string>();
        const refMap = new Map<string, LeadRefEntry>();
        for (const e of usersRes.items) {
          const u = e as UserEntity;
          const email = u.spec?.profile?.email;
          if (email) {
            if (u.spec?.profile?.picture)
              pictureMap.set(email, u.spec.profile.picture);
            entityMap.set(email, u.metadata.name);
          }
          // Key by normalized entity ref so the new `team/lead` annotation
          // (entity-ref format) can be resolved without a per-row async call.
          refMap.set(stringifyEntityRef(u).toLowerCase(), {
            name: u.spec?.profile?.displayName ?? u.metadata.name,
            picture: u.spec?.profile?.picture,
            entityName: u.metadata.name,
          });
        }
        setLeadPictureMap(pictureMap);
        setLeadEntityMap(entityMap);
        setLeadRefMap(refMap);
        // Built from activeGroups, not the unfiltered list — otherwise a
        // member who only belongs to an archived (hidden) team would still
        // inflate that team's still-active ancestors' displayed counts.
        setMemberCountMap(
          buildMemberCountMap(activeGroups, usersRes.items as UserEntity[]),
        );
      })
      .catch(err => setError(String(err)))
      .finally(() => setLoading(false));
  }, [catalogApi]);

  const tree = useMemo(() => buildTree(entities), [entities]);
  // Root rows only mount once `!loading && !error`, i.e. after the catalog data
  // has resolved, so each row's `useState` seed already sees the final set.
  const initialExpanded = useMemo(() => computeInitialExpanded(tree), [tree]);

  const totalTeams = entities.filter(e => e.spec?.type === 'team').length;
  // matchesSearch walks the whole subtree per row, so filtering on every
  // keystroke is expensive once there are many teams — most noticeable when
  // backspacing broadens the match set (more rows re-appear) rather than
  // narrowing it. Deferring lets React keep the input itself responsive
  // while the tree catches up a beat behind.
  const deferredSearch = useDeferredValue(search);

  return (
    <Page themeId="home">
      <Header
        title="Teams"
        subtitle={
          loading
            ? ''
            : `${totalTeams} team${totalTeams !== 1 ? 's' : ''} across ${entities.length} groups`
        }
      />
      <Content>
        <Box display="flex" alignItems="center" mb={3}>
          <TextField
            className={classes.searchBox}
            style={{ marginBottom: 0 }}
            variant="outlined"
            size="small"
            placeholder="Search teams by name or mission…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {!readOnly && (
            <Box ml={2} flexShrink={0}>
              <Link to="/create-team">
                <Chip
                  icon={<AddIcon />}
                  label="Create a new team"
                  clickable
                  color="primary"
                  variant="outlined"
                />
              </Link>
            </Box>
          )}
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}

        {error && <Typography color="error">{error}</Typography>}

        {!loading && !error && (
          <Paper variant="outlined">
            <Box padding={1} className={classes.treeRoot}>
              {tree.length === 0 ? (
                <Typography className={classes.empty}>
                  No teams found in the catalog yet.
                </Typography>
              ) : (
                tree.map(node => (
                  <TreeRow
                    key={node.entity.metadata.name}
                    node={node}
                    depth={0}
                    searchQuery={deferredSearch}
                    leadPictureMap={leadPictureMap}
                    leadEntityMap={leadEntityMap}
                    leadRefMap={leadRefMap}
                    memberCountMap={memberCountMap}
                    readOnly={readOnly}
                    initialExpanded={initialExpanded}
                    openOverrides={openOverrides}
                    onToggle={handleToggle}
                    editLinkSuffix={editLinkSuffix}
                  />
                ))
              )}
            </Box>
          </Paper>
        )}
      </Content>
    </Page>
  );
}
